import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';
import { UserEnvironmentService } from '@examdojo/core/environment';
import { UntilDestroy } from '@ngneat/until-destroy';
import { combineLatest, distinctUntilChanged, fromEvent, map, of, shareReplay, startWith, tap } from 'rxjs';
import { ColorScheme } from './color-scheme.enum';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class ColorSchemeService {
  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Optional() private readonly userEnvironmentService: UserEnvironmentService,
  ) {
    this.applyColorScheme().subscribe();
  }

  private readonly colorScheme$ = this.userEnvironmentService?.appTheme$ || of(ColorScheme.System);
  private readonly hasDarkModeColorScheme = window.matchMedia('(prefers-color-scheme: dark)');
  private readonly systemHasDarkMode$ = fromEvent<MediaQueryListEvent>(this.hasDarkModeColorScheme, 'change').pipe(
    map((evt) => evt.matches),
    startWith(this.hasDarkModeColorScheme.matches),
  );

  readonly hasDarkMode$ = combineLatest([this.colorScheme$, this.systemHasDarkMode$]).pipe(
    map(([appTheme, systemHasDarkMode]) => {
      switch (appTheme) {
        case ColorScheme.Dark:
          return true;
        case ColorScheme.Light:
          return false;
        case ColorScheme.System:
        default:
          return systemHasDarkMode;
      }
    }),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private _hasDarkMode = false;

  get hasDarkMode() {
    return this._hasDarkMode;
  }

  private applyColorScheme() {
    return this.hasDarkMode$.pipe(
      tap((hasDarkMode) => (this._hasDarkMode = hasDarkMode)),
      tap((hasDarkMode) => (hasDarkMode ? this.setDarkMode() : this.setLightMode())),
    );
  }

  private setDarkMode(): void {
    this.document.body.classList.remove('light');
    this.document.body.classList.add('dark');
  }

  private setLightMode(): void {
    this.document.body.classList.remove('dark');
    this.document.body.classList.add('light');
  }
}
